import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Package, 
  ShoppingCart, 
  Star, 
  Settings, 
  TrendingUp, 
  DollarSign,
  Clock,
  Bell,
  Eye,
  Plus,
  BarChart3,
  Users,
  Calendar,
  AlertCircle,
  CheckCircle,
  Truck,
  MapPin
} from "lucide-react";
import { DashboardLayout } from "@/components/florist-dashboard/DashboardLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch dashboard analytics
  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['dashboard-analytics', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Get store profile
      const { data: storeProfile } = await supabase
        .from('store_profiles')
        .select('id')
        .eq('florist_id', user.id)
        .single();

      if (!storeProfile) return null;

      // Get orders data
      const { data: orders } = await supabase
        .from('orders')
        .select('*')
        .eq('florist_id', storeProfile.id);

      // Get recent orders (last 5)
      const { data: recentOrders } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            *,
            products (title, image_url)
          )
        `)
        .eq('florist_id', storeProfile.id)
        .order('created_at', { ascending: false })
        .limit(5);

      // Calculate analytics
      const totalOrders = orders?.length || 0;
      const pendingOrders = orders?.filter(o => o.status === 'awaiting_florist_confirmation').length || 0;
      const completedOrders = orders?.filter(o => 
        o.status === 'delivered' || 
        o.status === 'picked_up' || 
        o.status === 'succeeded'
      ).length || 0;
      const totalRevenue = orders?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0;
      
      // This month's data
      const thisMonth = new Date();
      thisMonth.setDate(1);
      const thisMonthOrders = orders?.filter(o => new Date(o.created_at) >= thisMonth) || [];
      const monthlyRevenue = thisMonthOrders.reduce((sum, order) => sum + (order.total_amount || 0), 0);

      return {
        totalOrders,
        pendingOrders,
        completedOrders,
        totalRevenue,
        monthlyRevenue,
        monthlyOrders: thisMonthOrders.length,
        recentOrders: recentOrders || []
      };
    },
    enabled: !!user?.id,
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'awaiting_florist_confirmation':
        return <AlertCircle className="h-4 w-4 text-[#843329]" />;
      case 'confirmed':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case 'preparing':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'out_for_delivery':
        return <Truck className="h-4 w-4 text-purple-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'awaiting_florist_confirmation':
        return 'bg-[#843329]/10 text-[#843329] border-[#843329]/20';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'preparing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'out_for_delivery':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back! Here's what's happening with your store.</p>
        </div>

        {/* Analytics Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {analyticsLoading ? (
            [...Array(4)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))
          ) : (
            <>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                  <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics?.totalOrders || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    {analytics?.monthlyOrders || 0} this month
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pending Orders</CardTitle>
                  <div className="flex items-center gap-1">
                    <Bell className="h-4 w-4 text-[#843329]" />
                    {(analytics?.pendingOrders || 0) > 0 && (
                      <Badge variant="destructive" className="h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                        {analytics?.pendingOrders}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-[#843329]">{analytics?.pendingOrders || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    Need your attention
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">${(analytics?.monthlyRevenue || 0).toFixed(2)}</div>
                  <p className="text-xs text-muted-foreground">
                    Total: ${(analytics?.totalRevenue || 0).toFixed(2)}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {analytics?.totalOrders ? Math.round((analytics.completedOrders / analytics.totalOrders) * 100) : 0}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {analytics?.completedOrders || 0} completed
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-brand-primary" />
              Quick Actions
            </CardTitle>
            <p className="text-sm text-muted-foreground">Common tasks to manage your store</p>
          </CardHeader>
          <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Button
                variant="outline"
                className="flex flex-col items-center justify-center h-24 space-y-2 bg-[#967F81] hover:bg-[#843329]/5 hover:border-[#843329]/20"
            onClick={() => navigate("/products/new")}
          >
                <div className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-brand-primary" />
                  <Package className="h-5 w-5 text-brand-primary" />
                </div>
                <div className="text-center">
                  <div className="font-medium">Add Product</div>
                  <div className="text-xs text-muted-foreground">Create new arrangements</div>
                </div>
          </Button>

          <Button
                variant="outline"
                className="flex flex-col items-center justify-center h-24 space-y-2 bg-[#967F81] hover:bg-[#843329]/5 hover:border-[#843329]/20 relative"
            onClick={() => navigate("/orders")}
          >
                <div className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-brand-primary" />
                  <ShoppingCart className="h-5 w-5 text-brand-primary" />
                  {(analytics?.pendingOrders || 0) > 0 && (
                    <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                      {analytics?.pendingOrders}
                    </Badge>
                  )}
                </div>
                <div className="text-center">
                  <div className="font-medium">View Orders</div>
                  <div className="text-xs text-muted-foreground">Manage customer orders</div>
                </div>
          </Button>

          <Button
                variant="outline"
                className="flex flex-col items-center justify-center h-24 space-y-2 bg-[#967F81] hover:bg-[#843329]/5 hover:border-[#843329]/20"
            onClick={() => navigate("/florist-dashboard/analytics")}
          >
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-brand-primary" />
                  <TrendingUp className="h-5 w-5 text-brand-primary" />
                </div>
                <div className="text-center">
                  <div className="font-medium">Analytics</div>
                  <div className="text-xs text-muted-foreground">View sales & insights</div>
                </div>
          </Button>

          <Button
                variant="outline"
                className="flex flex-col items-center justify-center h-24 space-y-2 bg-[#967F81] hover:bg-[#843329]/5 hover:border-[#843329]/20"
            onClick={() => navigate("/store-settings")}
          >
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-brand-primary" />
                  <Users className="h-5 w-5 text-brand-primary" />
                </div>
                <div className="text-center">
                  <div className="font-medium">Store Settings</div>
                  <div className="text-xs text-muted-foreground">Configure your store</div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Orders Preview */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-brand-primary" />
                  Recent Orders
                </CardTitle>
                <p className="text-sm text-muted-foreground">Your latest customer orders</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate("/orders")}>
                View All Orders
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {analyticsLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : analytics?.recentOrders?.length === 0 ? (
              <div className="text-center py-8">
                <Package className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <p className="mt-2 text-lg font-medium">No orders yet</p>
                <p className="text-sm text-muted-foreground">
                  Orders will appear here once customers start placing them
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {analytics?.recentOrders?.slice(0, 5).map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(order.status)}
                        <div>
                          <div className="font-medium">Order #{order.order_number || order.id.slice(0, 8)}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            <Calendar className="h-3 w-3" />
                            {new Date(order.created_at).toLocaleDateString()}
                            {order.is_delivery && (
                              <>
                                <MapPin className="h-3 w-3" />
                                <span>Delivery</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-medium">${order.total_amount?.toFixed(2)}</div>
                        <div className="text-sm text-muted-foreground">
                          {order.order_items?.length || 0} item(s)
                        </div>
                      </div>
                      
                      <Badge 
                        variant="outline" 
                        className={`${getStatusColor(order.status)} border`}
                      >
                        {order.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Badge>
                      
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        View
          </Button>
        </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation Help */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-brand-primary" />
              Where to Find Things
            </CardTitle>
            <p className="text-sm text-muted-foreground">Quick guide to navigating your dashboard</p>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Package className="h-5 w-5 text-brand-primary mt-0.5" />
                  <div>
                    <div className="font-medium">Products</div>
                    <div className="text-sm text-muted-foreground">Add, edit, and manage your flower arrangements and pricing</div>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <ShoppingCart className="h-5 w-5 text-brand-primary mt-0.5" />
                  <div>
                    <div className="font-medium">Orders</div>
                    <div className="text-sm text-muted-foreground">Accept orders, update status, and manage deliveries</div>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <BarChart3 className="h-5 w-5 text-brand-primary mt-0.5" />
                  <div>
                    <div className="font-medium">Analytics</div>
                    <div className="text-sm text-muted-foreground">View sales reports, customer insights, and performance metrics</div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Settings className="h-5 w-5 text-brand-primary mt-0.5" />
                  <div>
                    <div className="font-medium">Settings</div>
                    <div className="text-sm text-muted-foreground">Configure store details, delivery areas, and business hours</div>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Bell className="h-5 w-5 text-brand-primary mt-0.5" />
                  <div>
                    <div className="font-medium">Notifications</div>
                    <div className="text-sm text-muted-foreground">Manage email and SMS alerts for new orders and updates</div>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <DollarSign className="h-5 w-5 text-brand-primary mt-0.5" />
                  <div>
                    <div className="font-medium">Payment Settings</div>
                    <div className="text-sm text-muted-foreground">Set up Stripe Connect to receive payments directly</div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
} 